import { Component, Inject, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { of } from 'rxjs';
import { take } from 'rxjs/operators';

import { Category, CategoryService } from '../../../../core/lists';
import { TemplateService } from '../../../../core/lists/_services/template.service';
import { TaskMode } from '../enums';
import { TaskEntity, TaskTemplate } from '../models';
import { GeneralAutocompleteComponent } from '../../../partials/layout/general-autocomplete/general-autocomplete.component';
import { currentUser } from '../../../../core/auth';
import { Store } from '@ngrx/store';
import { AppState } from '../../../../core/reducers';

@Component({
  selector: 'kt-choose-template',
  templateUrl: 'choose-template.dialog.component.html',
  styleUrls: ['choose-template.dialog.component.scss'],
})
export class ChooseTemplateDialogComponent {
  submitted = false;
  fromTemplate = false;
  templateCtrl = new FormControl(null, Validators.required);
  getCategoryAutocomplete$ =
    this.categoryService.searchCategoriesWithoutInterventionPermission.bind(this.categoryService);
  getTemplateAutocomplete$ = this.templateService.findTaskTemplates.bind(
    this.templateService,
    this.getTemplatesFilter(),
  );
  @ViewChild('categoryAutocomplete') categoryAutocomplete: GeneralAutocompleteComponent;
  @ViewChild('templateAutocomplete') templateAutocomplete: GeneralAutocompleteComponent;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      fromNote?: boolean;
      category?: Category;
      relatedTemplates?: TaskTemplate[];
      entities?: TaskEntity[];
    },
    private dialogRef: MatDialogRef<ChooseTemplateDialogComponent>,
    private dialog: MatDialog,
    private categoryService: CategoryService,
    private templateService: TemplateService,
    private store: Store<AppState>,
  ) {
    let userInfo;
    this.store.select(currentUser).subscribe((res) => (userInfo = res));
    let userType = '';

    if (userInfo?.is_manager) userType = 'manager';
    if (userInfo?.userType === 'Admin') userType = 'admin';
    if (userInfo?.userType === 'User' && !userInfo?.is_manager) userType = 'user';

    this.getCategoryAutocomplete$ =
      this.categoryService.searchCategoriesWithoutInterventionPermission.bind(
        this.categoryService,
        userType,
      );

    if (this.data?.relatedTemplates?.length) this.fromTemplate = true;
  }

  getTemplatesFilter() {
    return {
      categoryId: this.data?.fromNote
        ? this.data?.category?.id
        : this.categoryAutocomplete?.value?.id,
      notAdditionalStep: true,
    };
  }

  formatCategoryOption(category: Category): string {
    return category?.name || '';
  }

  handleCategoryChange(): void {
    this.getTemplateAutocomplete$ = this.templateService.findTaskTemplates.bind(
      this.templateService,
      this.getTemplatesFilter(),
    );
  }

  formatTemplateOption(template: TaskTemplate): string {
    return template?.name || '';
  }

  close(event: Event, data?: any): void {
    event?.stopPropagation();

    this.dialogRef.close(data);
  }

  submit(event: Event): void {
    event.stopPropagation();

    if (this.submitted) return;

    const fromRelatedTemplate = !!this.data?.relatedTemplates?.length;
    const templateId = fromRelatedTemplate
      ? this.templateCtrl?.value?.id
      : this.templateAutocomplete?.value?.id;

    if (this.fromTemplate && !templateId) {
      if (fromRelatedTemplate) {
        this.templateCtrl.markAsTouched();
      } else {
        this.categoryAutocomplete?.updateValueAndValidity();
        this.templateAutocomplete?.updateValueAndValidity();
      }
      return;
    }
    this.submitted = true;

    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.closeOnNavigation = true;
    dialogConfig.width = '95vw';
    dialogConfig.maxWidth = '95vw';

    const getTemplateSteps$ = templateId
      ? this.templateService.getTaskTemplateSteps(templateId)
      : of({ data: { additional_step_template_dtos: null } });

    getTemplateSteps$.pipe(take(1)).subscribe((res1) => {
      if (!res1?.data) return;

      dialogConfig.data = {
        mode: TaskMode.CREATE,
        templateId: templateId,
        templateSteps: res1.data?.additional_step_template_dtos,
        entities: this.data?.entities,
      };

      // Loaded lazily: CreateTaskComponent opens this dialog, so a static import would be circular.
      import('../create-task/create-task.component').then(({ CreateTaskComponent }) => {
        const taskDialogRef = this.dialog.open(CreateTaskComponent, dialogConfig);

        taskDialogRef
          .afterClosed()
          .pipe(take(1))
          .subscribe((res2) => {
            this.submitted = false;

            if (res2) this.close(null, res2);
          });
      });
    });
  }
}
